import React, { useState } from 'react'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import TruncateMarkup from 'react-truncate-markup'
import parse from 'html-react-parser'
import { Place } from '@mui/icons-material'
import { Grid } from '@mui/material'

import { stripTags, getFullAddress } from 'utils/helpers'

import ProfileLinkedList from '../../../pages/profile/components/ProfileLinkedList.js'

export const DissolvedView = ({ name, description, funderAffiliations, businessNumber }) => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  return (
    <div className="profile-about__wrapper">
      <div className="profile-about__general-info">
        <h3>{name}</h3>
        {description !== null && <h4>{stripTags(description)}</h4>}

        <Grid container className="profile-about-info first">
          {Array.isArray(funderAffiliations) && !!funderAffiliations.length && (
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <ProfileLinkedList
                headerTitle={t.funder.affiliations}
                termData={funderAffiliations}
                RenderElement="li"
              />
            </Grid>
          )}
          {/* Business number should only display if it contains "RR" or "EIN" */}
          {businessNumber &&
            (businessNumber.search('RR') > -1 || businessNumber.search('EIN') > -1) && (
              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                <h4>{t.funder.bn}</h4>
                <span>{businessNumber}</span>
              </Grid>
            )}
        </Grid>
      </div>
    </div>
  )
}

export const ActiveView = ({
  addresses,
  businessNumber,
  description,
  faxNumber,
  funderAffiliations,
  funderPublications,
  language,
  mission,
  name,
  phoneNumber,
  phoneNumberExtension,
  primaryEmail,
  secondaryEmail,
  // TODO: Implement
  yearEstablished,
  contactNote
}) => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const [missionToggled, setMissionToggled] = useState(false)
  const toggleMission = e => {
    e.preventDefault()
    setMissionToggled(!missionToggled)
  }

  const funderAddress = Array.isArray(addresses) && addresses[0]
  const showContactInfo = phoneNumber || faxNumber || primaryEmail || secondaryEmail
  const readMoreEllipsis = (
    <span>
      <br />
      <a href="#" className="profile-about-mission-expand" onClick={toggleMission}>
        {t.global.seeMore}
      </a>
    </span>
  )

  return (
    <div className="profile-about__wrapper">
      <div className="profile-about__general-info">
        {name ? <h3>{name}</h3> : null}
        {description && <h4>{stripTags(description)}</h4>}
        {yearEstablished && (
          <div className="profile-about-year">
            <span>
              <strong>{`${t.funder.estabilished}: `}</strong>
              {yearEstablished}
            </span>
          </div>
        )}
        {mission && (
          <div className="profile-about-mission">
            <h4>{t.funder.mission}</h4>
            {missionToggled && (
              <div>
                <div>
                  <p dangerouslySetInnerHTML={{ __html: mission }} />
                </div>
                <a href="#" className="profile-about-mission-collapse" onClick={toggleMission}>
                  {t.global.seeLess}
                </a>
              </div>
            )}
            {!missionToggled && (
              <div>
                <TruncateMarkup lines={6} ellipsis={readMoreEllipsis}>
                  <div>{parse(mission)}</div>
                </TruncateMarkup>
              </div>
            )}
          </div>
        )}
        <Grid container columnSpacing={1} className="profile-about-info first">
          {Array.isArray(funderAffiliations) && !!funderAffiliations.length && (
            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
              <ProfileLinkedList
                headerTitle={t.funder.affiliations}
                termData={funderAffiliations}
                RenderElement="li"
              />
            </Grid>
          )}
          {Array.isArray(funderPublications) && !!funderPublications.length && (
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <h4>{t.funder.publications}</h4>
              {funderPublications.map((publication, index) => {
                return (
                  <li key={index}>
                    <a href={publication.url} target="_blank">
                      {publication.title}
                    </a>
                  </li>
                )
              })}
            </Grid>
          )}
          {/* Business number should only display if it contains "RR" or "EIN" */}
          {businessNumber &&
            (businessNumber.search('RR') > -1 || businessNumber.search('EIN') > -1) && (
              <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                <h4>{t.funder.bn}</h4>
                <span>{businessNumber}</span>
              </Grid>
            )}
        </Grid>
        <Grid container columnSpacing={1} className="profile-about-contact-info">
          <Grid size={{ md: 12 }}>
            <h4>{t.funder.contactInfo}</h4>
          </Grid>
          {funderAddress && (
            <Grid size={{ xs: 12, sm: 12, md: 5 }}>
              <Place />
              <a
                href={`https://www.google.ca/maps/place/${[
                  funderAddress.streetAddress1,
                  funderAddress.city,
                  funderAddress.province,
                  funderAddress.postalCode
                ]
                  .filter(Boolean)
                  .join(', ')}`}
                target="_blank"
              >
                {getFullAddress(funderAddress)}
              </a>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 12, md: 7 }}>
            <Grid container>
              {showContactInfo !== false && (
                <Grid size={{ xs: 12, sm: 12, md: 8 }}>
                  {phoneNumber !== null && (
                    <span>
                      <strong>{`${t.funder.phone}:`}</strong>
                      <br />
                      <span>{phoneNumber}</span>
                      {phoneNumberExtension !== null && (
                        <span>
                          , {t.funder.ext} {phoneNumberExtension}
                        </span>
                      )}
                    </span>
                  )}
                  <p></p>
                  {faxNumber && (
                    <span>
                      <strong>{`${t.funder.fax}:`}</strong>
                      <br />
                      <span>{faxNumber}</span>
                    </span>
                  )}
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                <span>
                  <strong>{`${t.global.language}:`}</strong>
                  <br />
                  {t.global.languages[language]}
                </span>
              </Grid>
            </Grid>
            <Grid container style={{ marginTop: 10 }}>
              <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                {showContactInfo !== false && (primaryEmail || secondaryEmail) && (
                  <span>
                    <strong>{`${t.global.email}:`}</strong>
                    {primaryEmail && (
                      <React.Fragment>
                        <br />
                        <span>
                          <a href={'mailto:' + primaryEmail}>{primaryEmail}</a>
                        </span>
                      </React.Fragment>
                    )}
                    {secondaryEmail && (
                      <React.Fragment>
                        <br />
                        <span>
                          <a href={'mailto:' + secondaryEmail}>{secondaryEmail}</a>
                        </span>
                      </React.Fragment>
                    )}
                  </span>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className="profile-about-contact-note">
          <Grid size={{ md: 12 }}>
            {contactNote && <p dangerouslySetInnerHTML={{ __html: contactNote }} />}
          </Grid>
        </Grid>
      </div>
    </div>
  )
}
